import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, BarChart3, Clock, Users, AlertTriangle, TrendingUp } from 'lucide-react';
import ReportsManagement from './Settings/ReportsManagement';

const ReportPage = ({ isDarkMode = false, timeFormat = "24h" }) => {
  const navigate = useNavigate();
  const [timezone, setTimezone] = useState(() => {
    const cachedTimezone = localStorage.getItem('cached_timezone');
    return cachedTimezone || 'Etc/UTC';
  });
  const [settingsTimezone, setSettingsTimezone] = useState(null);
  
  // Dynamic data state
  const [reportsData, setReportsData] = useState({
    totalReports: 0,
    highSeverity: 0,
    mediumSeverity: 0,
    lowSeverity: 0,
    recentReports: 0,
    totalAudioFiles: 0
  });
  const [loading, setLoading] = useState(true);
  
  // Modal state for ReportsManagement
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  
  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [incidentToDelete, setIncidentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Form state for modal
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startTime: '',
    endTime: '',
    severity: 'low'
  });

  const themeClasses = {
    primaryText: isDarkMode ? 'text-gray-100' : 'text-gray-800',
    secondaryText: isDarkMode ? 'text-gray-300' : 'text-gray-600',
    mutedText: isDarkMode ? 'text-gray-400' : 'text-gray-500',
    accent: isDarkMode ? 'text-blue-400' : 'text-blue-600',
    cardHover: isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50',
    secondaryBtn: isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200',
    primaryBtn: isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600',
    border: isDarkMode ? 'border-gray-700' : 'border-gray-200',
    background: isDarkMode ? 'bg-gray-900' : 'bg-white',
    cardBackground: isDarkMode ? 'bg-gray-800' : 'bg-white',
    gradientBg: isDarkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-indigo-50',
    glassBg: isDarkMode ? 'bg-gray-800/80 backdrop-blur-md' : 'bg-white/80 backdrop-blur-md',
  };

  // Fetch timezone from settings
  useEffect(() => {
    const fetchTimezone = async () => {
      try {
        const edgeServerEndpoint = process.env.REACT_APP_EDGE_SERVER_ENDPOINT || "http://localhost:4000";
        const settingsResp = await fetch(`${edgeServerEndpoint}/settings`).catch(() => null);
        if (settingsResp && settingsResp.ok) {
          const settingsData = await settingsResp.json();
          const tz = settingsData?.global_timezone;
          if (tz) {
            try {
              new Intl.DateTimeFormat('en-US', { timeZone: tz });
              setSettingsTimezone(tz);
              setTimezone(tz);
              localStorage.setItem('cached_timezone', tz);
            } catch (err) {
              console.warn('Invalid timezone from settings:', tz);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching timezone settings:', error);
      }
    };
    fetchTimezone();
  }, []);

  // Fetch reports data
  useEffect(() => {
    const fetchReportsData = async () => {
      try {
        const edgeServerEndpoint = process.env.REACT_APP_EDGE_SERVER_ENDPOINT || "http://localhost:4000";
        const response = await fetch(`${edgeServerEndpoint}/incident-reports`);
        if (!response.ok) throw new Error('Failed to fetch reports');
        
        const data = await response.json();
        
        // Calculate statistics
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        const stats = {
          totalReports: data.length,
          highSeverity: data.filter(r => r.severity.toLowerCase() === 'high').length,
          mediumSeverity: data.filter(r => r.severity.toLowerCase() === 'medium').length,
          lowSeverity: data.filter(r => r.severity.toLowerCase() === 'low').length,
          recentReports: data.filter(r => new Date(r.created_at) > oneWeekAgo).length,
          totalAudioFiles: data.reduce((sum, r) => sum + (r.messages?.length || 0), 0)
        };
        
        setReportsData(stats);
      } catch (error) {
        console.error('Error fetching reports data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReportsData();
  }, []);

  // Format date for datetime-local input (YYYY-MM-DDTHH:MM:SS) with timezone conversion
  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return '';
    
    // Try parsing the date string
    let date = new Date(dateString);
    
    // If parsing fails, try removing milliseconds
    if (isNaN(date.getTime())) {
      const withoutMilliseconds = dateString.replace(/\.\d+/, '');
      date = new Date(withoutMilliseconds);
    }
    
    // If still invalid, return empty string
    if (isNaN(date.getTime())) return '';
    
    // Use settingsTimezone if available, otherwise try localStorage, fallback to UTC
    let timezone = settingsTimezone || localStorage.getItem('cached_timezone') || 'Etc/UTC';
    
    // Validate timezone before using it
    const validateAndFixTimezone = (tz) => {
      try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return tz;
      } catch (error) {
        console.warn(`Invalid timezone "${tz}", falling back to Etc/UTC`);
        return 'Etc/UTC';
      }
    };
    
    timezone = validateAndFixTimezone(timezone);
    
    // Use Intl.DateTimeFormat to get parts in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(date);
    const year = parts.find(p => p.type === 'year')?.value || '';
    const month = parts.find(p => p.type === 'month')?.value || '';
    const day = parts.find(p => p.type === 'day')?.value || '';
    const hour = parts.find(p => p.type === 'hour')?.value || '';
    const minute = parts.find(p => p.type === 'minute')?.value || '';
    const second = parts.find(p => p.type === 'second')?.value || '';
    
    return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  };

  // Handle form submission
  const handleUpdateIncident = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    
    try {
      // Convert datetime-local input value back to UTC for server storage
      // The user entered a time that was displayed in the configured timezone
      // but the browser interprets datetime-local as browser local time
      // We need to find what UTC time corresponds to the entered time in the configured timezone
      const convertLocalToUTC = (localDateTime) => {
        if (!localDateTime) return "";
        
        // Get the configured timezone
        const tz = settingsTimezone || localStorage.getItem('cached_timezone') || 'Etc/UTC';
        
        // Parse the datetime-local string (YYYY-MM-DDTHH:MM:SS)
        const [datePart, timePart] = localDateTime.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds = 0] = (timePart || '00:00:00').split(':').map(Number);
        
        // Find the UTC time that, when formatted in the target timezone, equals the input
        // We'll use binary search to find the correct UTC time
        const startOfDay = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
        const endOfDay = new Date(Date.UTC(year, month - 1, day + 1, 0, 0, 0));
        let low = startOfDay.getTime();
        let high = endOfDay.getTime();
        let bestMatch = new Date((low + high) / 2);
        
        for (let i = 0; i < 50; i++) {
          const mid = new Date((low + high) / 2);
          
          // Format this UTC time in the target timezone
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });
          
          const parts = formatter.formatToParts(mid);
          const tzYear = parseInt(parts.find(p => p.type === 'year')?.value || '0');
          const tzMonth = parseInt(parts.find(p => p.type === 'month')?.value || '0');
          const tzDay = parseInt(parts.find(p => p.type === 'day')?.value || '0');
          const tzHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
          const tzMinute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
          const tzSecond = parseInt(parts.find(p => p.type === 'second')?.value || '0');
          
          // Check if this matches what we want
          if (tzYear === year && tzMonth === month && tzDay === day &&
              tzHour === hours && tzMinute === minutes && tzSecond === seconds) {
            return mid.toISOString();
          }
          
          // Compare lexicographically to adjust search range
          const needIncrease = 
            tzYear < year ||
            (tzYear === year && tzMonth < month) ||
            (tzYear === year && tzMonth === month && tzDay < day) ||
            (tzYear === year && tzMonth === month && tzDay === day && tzHour < hours) ||
            (tzYear === year && tzMonth === month && tzDay === day && tzHour === hours && tzMinute < minutes) ||
            (tzYear === year && tzMonth === month && tzDay === day && tzHour === hours && tzMinute === minutes && tzSecond < seconds);
          
          if (needIncrease) {
            low = mid.getTime() + 1;
          } else {
            high = mid.getTime() - 1;
          }
          
          bestMatch = mid;
        }
        
        // Return the best match we found (should be very close)
        return bestMatch.toISOString();
      };

      const edgeServerEndpoint = process.env.REACT_APP_EDGE_SERVER_ENDPOINT || "http://localhost:4000";
      
      const response = await fetch(`${edgeServerEndpoint}/incident-reports/${selectedIncident.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          startTime: formData.startTime ? convertLocalToUTC(formData.startTime) : selectedIncident.startTime,
          endTime: formData.endTime ? convertLocalToUTC(formData.endTime) : selectedIncident.endTime,
          severity: formData.severity.charAt(0).toUpperCase() + formData.severity.slice(1),
          channels_involved: selectedIncident.location.split(', '),
          messages: selectedIncident.audios.map(audio => ({
            id: parseInt(audio.id.replace('aud-', '')),
            time: audio.recordedAt,
            message: audio.transcription,
            channel: audio.source.replace('Channel ', ''),
            url: audio.url
          })),
          messageCount: selectedIncident.audios.length,
        }),
      });

      if (!response.ok) throw new Error('Failed to update incident');
      
      // Close modal and reset form
      setIsUpdateModalOpen(false);
      setFormData({
        name: '',
        description: '',
        startTime: '',
        endTime: '',
        severity: 'low'
      });
      
      // Refresh the page to show updated data
      window.location.reload();
      
    } catch (err) {
      console.error('Update failed:', err);
      alert('Failed to update incident. Please try again.');
    } finally {
      setUpdateLoading(false);
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isUpdateModalOpen && selectedIncident) {
      setFormData({
        name: selectedIncident.name || selectedIncident.title || '',
        description: selectedIncident.description || '',
        startTime: selectedIncident.startTime ? formatDateTimeForInput(selectedIncident.startTime) : '',
        endTime: selectedIncident.endTime ? formatDateTimeForInput(selectedIncident.endTime) : '',
        severity: selectedIncident.severity ? selectedIncident.severity.toLowerCase() : 'low'
      });
    }
  }, [isUpdateModalOpen, selectedIncident]);

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!incidentToDelete) return;
    
    setDeleteLoading(true);
    
    try {
      const edgeServerEndpoint = process.env.REACT_APP_EDGE_SERVER_ENDPOINT || "http://localhost:4000";
      
      const response = await fetch(`${edgeServerEndpoint}/incident-reports/${incidentToDelete.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete incident');
      
      // Close modal and refresh the page to show updated data
      setIsDeleteModalOpen(false);
      setIncidentToDelete(null);
      window.location.reload();
      
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete incident. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle delete request (opens confirmation modal)
  const handleDeleteRequest = (incident) => {
    setIncidentToDelete(incident);
    setIsDeleteModalOpen(true);
  };

  return (
    <div className={`min-h-screen ${themeClasses.gradientBg}`}>
      {/* Enhanced Header */}
      <div className={`sticky top-0 z-30 ${themeClasses.glassBg} border-b ${themeClasses.border} shadow-lg backdrop-blur-md`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-6">
              <button
                onClick={() => navigate(-1)}
                className={`p-3 rounded-xl ${themeClasses.cardHover} ${themeClasses.secondaryBtn} transition-all duration-300 shadow-md hover:shadow-lg group`}
                aria-label="Go back"
              >
                <ArrowLeft className={`w-5 h-5 ${themeClasses.primaryText} group-hover:scale-110 transition-transform duration-200`} />
              </button>
              <div className="flex items-center space-x-4">
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'} shadow-md`}>
                  <FileText className={`w-8 h-8 ${themeClasses.accent}`} />
                </div>
                <div>
                  <h1 className={`text-2xl font-bold ${themeClasses.primaryText} tracking-tight`}>
                    Incident Reports
                  </h1>
                  <p className={`text-sm ${themeClasses.mutedText} mt-1`}>
                    {loading ? 'Loading statistics...' : `${reportsData.totalReports} total reports`}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Stats Cards */}
            <div className="hidden md:flex items-center space-x-4">
              {/* <div className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-white/50'} border ${themeClasses.border} shadow-sm`}>
                <div className="flex items-center space-x-2">
                  <BarChart3 className={`w-4 h-4 ${themeClasses.accent}`} />
                  <span className={`text-sm font-medium ${themeClasses.primaryText}`}>Analytics</span>
                </div>
              </div> */}
              <div className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-white/50'} border ${themeClasses.border} shadow-sm`}>
                <div className="flex items-center space-x-2">
                  <Clock className={`w-4 h-4 ${themeClasses.accent}`} />
                  <span className={`text-sm font-medium ${themeClasses.primaryText}`}>{timezone}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dynamic Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className={`p-6 rounded-2xl ${themeClasses.glassBg} border ${themeClasses.border} shadow-lg hover:shadow-xl transition-all duration-300`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${themeClasses.mutedText} mb-1`}>Total Reports</p>
                <p className={`text-3xl font-bold ${themeClasses.primaryText}`}>
                  {loading ? '...' : reportsData.totalReports}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                <FileText className={`w-6 h-6 ${themeClasses.accent}`} />
              </div>
            </div>
            <div className="mt-4 flex items-center space-x-2">
              <TrendingUp className={`w-4 h-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              <span className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                {reportsData.recentReports} this week
              </span>
            </div>
          </div>
          
          <div className={`p-6 rounded-2xl ${themeClasses.glassBg} border ${themeClasses.border} shadow-lg hover:shadow-xl transition-all duration-300`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${themeClasses.mutedText} mb-1`}>High Priority</p>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                  {loading ? '...' : reportsData.highSeverity}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
                <AlertTriangle className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
              </div>
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${isDarkMode ? 'bg-red-400' : 'bg-red-500'}`}
                  style={{ width: `${reportsData.totalReports > 0 ? (reportsData.highSeverity / reportsData.totalReports) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          </div>
          
          <div className={`p-6 rounded-2xl ${themeClasses.glassBg} border ${themeClasses.border} shadow-lg hover:shadow-xl transition-all duration-300`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${themeClasses.mutedText} mb-1`}>Audio Files</p>
                <p className={`text-3xl font-bold ${themeClasses.primaryText}`}>
                  {loading ? '...' : reportsData.totalAudioFiles}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                <Users className={`w-6 h-6 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              </div>
            </div>
            <div className="mt-4">
              <p className={`text-sm ${themeClasses.mutedText}`}>
                Avg: {reportsData.totalReports > 0 ? Math.round(reportsData.totalAudioFiles / reportsData.totalReports) : 0} per report
              </p>
            </div>
          </div>
        </div>
        
        {/* Reports Management Component */}
        <div className={`rounded-2xl ${themeClasses.glassBg} border ${themeClasses.border} shadow-xl overflow-hidden`}>
          <ReportsManagement 
            isDarkMode={isDarkMode} 
            isUpdateModalOpen={isUpdateModalOpen}
            setIsUpdateModalOpen={setIsUpdateModalOpen}
            selectedIncident={selectedIncident}
            setSelectedIncident={setSelectedIncident}
            updateLoading={updateLoading}
            setUpdateLoading={setUpdateLoading}
            onDeleteRequest={handleDeleteRequest}
            deleteLoading={deleteLoading}
          />
        </div>
      </div>

      {/* MODAL - Rendered at page level to avoid positioning issues */}
      {isUpdateModalOpen && selectedIncident && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] transition-opacity duration-300 ease-out">
          <div className={`rounded-xl p-8 w-full max-w-lg mx-4 ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-2xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} tracking-tight`}>
                Update Incident
              </h2>
              <button
                onClick={() => setIsUpdateModalOpen(false)}
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} transition-colors duration-200`}
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdateIncident} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Incident Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full p-3 rounded-lg border ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-700 text-gray-100 focus:ring-blue-500 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                  } transition-all duration-200 focus:ring-2 focus:ring-offset-2`}
                  required
                  aria-required="true"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`w-full p-3 rounded-lg border ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-700 text-gray-100 focus:ring-blue-500 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                  } transition-all duration-200 focus:ring-2 focus:ring-offset-2`}
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.startTime}
                    step="1"
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    className={`w-full p-3 rounded-lg border ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-700 text-gray-100 focus:ring-blue-500 focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                    } transition-all duration-200 focus:ring-2 focus:ring-offset-2`}
                    required
                    aria-required="true"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    End Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.endTime}
                    step="1"
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className={`w-full p-3 rounded-lg border ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-700 text-gray-100 focus:ring-blue-500 focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                    } transition-all duration-200 focus:ring-2 focus:ring-offset-2`}
                    required
                    aria-required="true"
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Severity <span className="text-red-500">*</span>
                  <span
                    className="ml-2 text-xs text-gray-500 cursor-help"
                    title="Low: Minor impact, Medium: Moderate impact, High: Critical impact"
                  >
                    (?)
                  </span>
                </label>
                <select
                  value={formData.severity}
                  onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                  className={`w-full p-3 rounded-lg border ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-700 text-gray-100 focus:ring-blue-500 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                  } transition-all duration-200 focus:ring-2 focus:ring-offset-2`}
                  required
                  aria-required="true"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="mt-8 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsUpdateModalOpen(false)}
                  className={`px-5 py-2.5 rounded-lg ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors duration-200 focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateLoading || formData.name === '' || formData.startTime === '' || formData.endTime === '' || formData.severity === ''}
                  className={`px-5 py-2.5 rounded-lg ${
                    isDarkMode
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  } transition-colors duration-200 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {updateLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {isDeleteModalOpen && incidentToDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] transition-opacity duration-300 ease-out">
          <div className={`rounded-xl p-8 w-full max-w-md mx-4 ${isDarkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}>
            <div className="flex items-center space-x-3 mb-6">
              <div className={`p-3 rounded-full ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
                <svg className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} tracking-tight`}>
                  Delete Incident Report
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                  This action cannot be undone
                </p>
              </div>
            </div>

            <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                <span className="font-medium">Incident:</span> {incidentToDelete.title || incidentToDelete.name}
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <span className="font-medium">Created:</span> {new Date(incidentToDelete.date || incidentToDelete.created_at).toLocaleDateString()}
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <span className="font-medium">Audio Files:</span> {incidentToDelete.audios?.length || 0}
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setIncidentToDelete(null);
                }}
                disabled={deleteLoading}
                className={`px-5 py-2.5 rounded-lg ${
                  isDarkMode
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } transition-colors duration-200 focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className={`px-5 py-2.5 rounded-lg ${
                  isDarkMode
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-red-500 text-white hover:bg-red-600'
                } transition-colors duration-200 focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {deleteLoading ? (
                  <div className="flex items-center space-x-2">
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <span>Deleting...</span>
                  </div>
                ) : (
                  'Delete Report'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPage; 